import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import useAuth from "../hooks/useAuth";
import { canDo, isSuper } from '../utils/canDo';
import api, { getGpsCache } from "../components/api";
import { useSessionManager } from '../context/SessionManagerContext';
import { useNotifications } from '../context/NotificationContext';
import InstallPrompt from '../components/InstallPrompt';
import { STRIP_HEIGHT } from '../components/SessionTabStrip';

const ADMIN_ROLES = ['owner', 'admin', 'superadmin', 'supervisor'];
const GDRIVE_ROLES = ['owner', 'admin', 'superadmin', 'solution'];

// ── Helpers ───────────────────────────────────────────────────────────────────
function decodeJwtIat(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.iat ? new Date(payload.iat * 1000) : null;
  } catch (e) { return null; }
}

function timeAgo(date) {
  if (!date) return null;
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

// ── User avatar (initials fallback) ──────────────────────────────────────────
function UserAvatar({ username, size = 34 }) {
  const colors = ['#6366f1','#0ea5e9','#10b981','#f97316','#ec4899','#8b5cf6'];
  let h = 0;
  for (let i = 0; i < (username || '').length; i++)
    h = (h * 31 + username.charCodeAt(i)) % colors.length;
  const bg  = colors[h];
  const ini = username ? username.slice(0, 2).toUpperCase() : 'ME';
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: `linear-gradient(135deg,${bg}dd,${bg}88)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontWeight: 800, fontSize: Math.round(size * 0.38),
      flexShrink: 0, border: '2px solid rgba(255,255,255,0.18)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.30)',
      userSelect: 'none',
    }}>
      {ini}
    </div>
  );
}

const MainLayout = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const sm = useSessionManager();

  // ── Responsive state ─────────────────────────────────────────────────────
  const [menuOpen,      setMenuOpen]      = useState(() => typeof window !== 'undefined' && window.innerWidth > 1024);
  const [isTablet,      setIsTablet]      = useState(() => typeof window !== 'undefined' && window.innerWidth <= 1024);
  const [isMobile,      setIsMobile]      = useState(() => typeof window !== 'undefined' && window.innerWidth <= 480);
  const [pendingCount,   setPendingCount]   = useState(0);
  // liveCount is now derived from SessionManagerContext (kept live via socket events)
  // No polling needed — socket:session:registered / session:ended updates the context.
  const [showNotifPanel,   setShowNotifPanel]   = useState(false);
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  const [installPrompt,    setInstallPrompt]    = useState(null);
  const [showMobileMenu,   setShowMobileMenu]   = useState(false);
  const [liveFooterOpen,   setLiveFooterOpen]   = useState(true); // default expanded
  const [adminOpen,        setAdminOpen]        = useState(() => {
    // Auto-open if current page is an admin page
    const adminPaths = ['/PaymentsLedger','/ChecklistsDashboard','/PatientIrregular','/PatientDashboard','/Services','/Recomms','/Mappings','/Exercises','/BPSelection','/Branches','/Orgs','/AttendanceReport','/Policies','/usermgmt','/Payroll','/DanglingEntries'];
    return adminPaths.some(p => window.location.pathname === p);
  });

  const pendingTimerRef = useRef(null);
  const notifPanelRef   = useRef(null);
  const notifDropRef    = useRef(null);
  const profilePanelRef = useRef(null);
  const notifPosRef     = useRef({ top: 58, right: 8 });
  const profilePosRef   = useRef({ top: 58, right: 8 });

  const handleToggleNotif = () => {
    if (notifPanelRef.current) {
      const r = notifPanelRef.current.getBoundingClientRect();
      notifPosRef.current = { top: Math.round(r.bottom + 4), right: Math.max(4, Math.round(window.innerWidth - r.right)) };
    }
    setShowNotifPanel(v => !v);
    setShowProfilePanel(false);
  };
  const handleToggleProfile = () => {
    if (profilePanelRef.current) {
      const r = profilePanelRef.current.getBoundingClientRect();
      profilePosRef.current = { top: Math.round(r.bottom + 4), right: Math.max(4, Math.round(window.innerWidth - r.right)) };
    }
    setShowProfilePanel(v => !v);
    setShowNotifPanel(false);
  };

  // Auto-dismiss notification dropdown after 3 seconds on iPad/laptop
  useEffect(() => {
    if (!showNotifPanel) return;
    const t = setTimeout(() => setShowNotifPanel(false), 3000);
    return () => clearTimeout(t);
  }, [showNotifPanel]);

  const { notifications: allNotifications = [], sessionPendingNotifs = [], unreadCount = 0 } = useNotifications() || {};
  // Recent 5 unresolved notifications (all types) for bell dropdown
  const recentNotifs = allNotifications.filter(n => !n.resolved).slice(0, 5);

  // ── Roles (defined early so isSaadhak can use them) ─────────────────────
  const userRoles = auth?.roles || [];
  const isAdmin   = userRoles.some(r => ADMIN_ROLES.includes(r));
  const isSaadhak = userRoles.includes('saadhak');
  const displayDesignation = auth?.designation || (isSaadhak ? 'Saadhak' : userRoles.includes('owner') ? 'Owner' : userRoles.includes('supervisor') ? 'Supervisor' : userRoles.includes('admin') ? 'Admin' : 'Therapist');
  const loginAt   = decodeJwtIat(auth?.token);
  // Decode remote-access flags from JWT
  const jwtPayload = (() => { try { return JSON.parse(atob((auth?.token || '').split('.')[1])); } catch (e) { return {}; } })();
  const hasRemoteKit = jwtPayload.remoteKitAccess === true;
  const hasRemoteExpense = jwtPayload.remoteExpenseAccess === true;
  const hasRemoteTask = jwtPayload.remoteTaskAccess === true;
  const LIMITED_REMOTE_PATHS = ['/TherapistDashboard', ...(hasRemoteKit ? ['/KIT'] : []), ...(hasRemoteExpense ? ['/ManageExpenses'] : []), ...(hasRemoteTask ? ['/TaskManagerPage', '/ChecklistsDashboard'] : [])];

  // ── Geo-fence awareness: inside fence → full permissions, outside → limitedRemoteAccess applies
  const [isInsideGeoFence, setIsInsideGeoFence] = useState(true); // default true (fail-open, like backend)
  const branchCoordsRef = useRef(null);
  const geoRadiusRef = useRef(80);
  useEffect(() => {
    if (isSuper(auth) || jwtPayload.allowedOutside) return; // no need to check
    // Fetch branch geo-coords + configurable radius
    Promise.all([
      api.get(`${process.env.REACT_APP_URL}/branches`),
      api.get(`${process.env.REACT_APP_URL}/attendance-config`).catch(() => ({ data: {} })),
    ]).then(([brRes, cfgRes]) => {
      geoRadiusRef.current = cfgRes.data?.middlewareGeoRadius || 80;
      const coords = (brRes.data || [])
        .filter(b => b.isActive !== false && b.siteLat && b.siteLon)
        .map(b => ({ lat: parseFloat(b.siteLat), lon: parseFloat(b.siteLon) }))
        .filter(c => !isNaN(c.lat) && !isNaN(c.lon));
      branchCoordsRef.current = coords;
      _checkGeoFence(coords);
    }).catch(() => {});

    function _distM(lat1, lon1, lat2, lon2) {
      const R = 6371000, r = d => d * Math.PI / 180;
      const dLat = r(lat2 - lat1), dLon = r(lon2 - lon1);
      const a = Math.pow(Math.sin(dLat / 2), 2) + Math.cos(r(lat1)) * Math.cos(r(lat2)) * Math.pow(Math.sin(dLon / 2), 2);
      return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }
    function _checkGeoFence(coords) {
      const gps = getGpsCache();
      if (!gps || !coords || !coords.length) { setIsInsideGeoFence(true); return; } // fail-open
      const inside = coords.some(c => _distM(gps.lat, gps.lon, c.lat, c.lon) <= geoRadiusRef.current);
      setIsInsideGeoFence(inside);
    }
    // Re-check every 3 min (matches GPS refresh interval)
    const iv = setInterval(() => { if (branchCoordsRef.current) _checkGeoFence(branchCoordsRef.current); }, 3 * 60 * 1000);
    return () => clearInterval(iv);
  }, [auth?.token]);

  const isLimitedRemote = !isSuper(auth) && jwtPayload.limitedRemoteAccess === true && !jwtPayload.allowedOutside && !isInsideGeoFence;

  const sessionList  = sm?.sessionList || []; // used for ding-state badge + dropdown rows

  // ── Fetch pending booking count ──────────────────────────────────────────
  const fetchPendingCount = useCallback(() => {
    if (!auth?.token || !isAdmin) return;
    api.get(`${process.env.REACT_APP_URL}/appointments/patient-bookings/pending`)
      .then(r => setPendingCount((r.data || []).length))
      .catch(() => {});
  }, [auth, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchPendingCount();
    pendingTimerRef.current = setInterval(fetchPendingCount, 60000);
    return () => clearInterval(pendingTimerRef.current);
  }, [fetchPendingCount]);

  // liveCount derived from context (no polling — socket keeps it live)
  const liveCount = sm?.sessionList?.length ?? 0;

  // ── Responsive ───────────────────────────────────────────────────────────
  useEffect(() => {
    const check = () => {
      setIsTablet(window.innerWidth <= 1024);
      setIsMobile(window.innerWidth <= 480);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // ── PWA Install prompt (Android) ─────────────────────────────────────────
  useEffect(() => {
    function isStandalone() {
      try { return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true; } catch (e) { return false; }
    }
    if (isStandalone()) return;
    // Pick up the deferred prompt captured by index.js before React mounted
    if (window.__pwaInstallPrompt) setInstallPrompt(window.__pwaInstallPrompt);
    function onPrompt(e) { e.preventDefault(); setInstallPrompt(e); window.__pwaInstallPrompt = e; }
    function onInstalled() { setInstallPrompt(null); window.__pwaInstallPrompt = null; }
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => { window.removeEventListener('beforeinstallprompt', onPrompt); window.removeEventListener('appinstalled', onInstalled); };
  }, []);

  function handleInstallClick() {
    if (!installPrompt) return;
    installPrompt.prompt();
    installPrompt.userChoice.then(function(r) { setInstallPrompt(null); });
  }

  // ── Close panels on outside click ────────────────────────────────────────
  useEffect(() => {
    const handler = e => {
      if (notifPanelRef.current && !notifPanelRef.current.contains(e.target) && !(notifDropRef.current && notifDropRef.current.contains(e.target)))
        setShowNotifPanel(false);
      if (profilePanelRef.current && !profilePanelRef.current.contains(e.target))
        setShowProfilePanel(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Menu items ───────────────────────────────────────────────────────────
  // ── Main menu items (visible to all applicable roles) ──────────────────
  const mainMenuItems = [
    { label: 'All Saadhak',           icon: '\uD83D\uDC65', path: '/ListPatients',       permission: 'patients.view', universal: true },
    { label: 'Therapist Dashboard',   icon: '\uD83E\uDE7A', path: '/TherapistDashboard', universal: true },
    { label: 'Live Sessions',         icon: '\uD83D\uDD34', path: '/LiveSessions',       permission: 'sessions.monitor' },
    { label: 'Appointments',          icon: '\uD83D\uDCC5', path: '/Cal',                permission: 'appointments.view' },
    { label: 'Billing',               icon: '\uD83E\uDDFE', path: '/Billing',            permission: 'billing.view' },
    // OTT Dashboard removed from menu — route still accessible at /MonthlyDashboard
    { label: 'OTT CheckList Manager', icon: '\u2611\uFE0F', path: '/TaskManagerPage',    permission: 'checklist.view' },
    { label: 'KIT',                   icon: '\uD83D\uDCE8', path: '/KIT',                permission: 'leads.kit' },
    { label: 'Online',                icon: '\uD83C\uDF10', path: '/Online',             permission: 'online.view' },
    { label: 'Flow Manager',          icon: '\u26A1',       path: '/FlowDashboard',      permission: 'flows.view' },
    { label: 'Resources',             icon: '\uD83D\uDCF9', path: '/Resources',          permission: 'resources.view' },
    { label: 'Inventory',             icon: '\uD83D\uDCE6', path: '/Inventory',          permission: 'inventory.view' },
    { label: 'Expenses',              icon: '\uD83D\uDCB8', path: '/ManageExpenses',     permission: 'expenses.view' },
    { label: 'My Attendance',         icon: '\uD83D\uDCCD', path: '/MyAttendance',       permission: 'attendance.view_own' },
    { label: 'Share App',             icon: '\uD83D\uDCF2', path: '/ShareApp',           permission: 'app.share' },
    // Saadhak items
    { label: 'My Reports',            icon: '\uD83D\uDCCB', path: '/MyReports',          saadhak: true, permission: 'reports.view' },
    { label: 'My Consultations',      icon: '\uD83E\uDE7A', path: '/MyConsultations',    saadhak: true },
    { label: 'Notifications',         icon: '\uD83D\uDD14', path: '/Notifications',      saadhak: true },
  ];

  // ── Admin submenu items ─────────────────────────────────────────────────
  const adminMenuItems = [
    { label: 'Payments Ledger',       icon: '\uD83D\uDCB3', path: '/PaymentsLedger',      permission: 'payments.view' },
    { label: 'Checklists Dashboard',  icon: '\uD83D\uDCCB', path: '/ChecklistsDashboard', permission: 'checklist.view' },
    { label: 'Irregular',             icon: '\u26A0\uFE0F', path: '/PatientIrregular',    permission: 'patients.irregular' },
    { label: 'Saadhak Dashboard',     icon: '\uD83D\uDC64', path: '/PatientDashboard',    permission: 'patients.dashboard' },
    { label: 'Services',              icon: '\uD83D\uDEE0\uFE0F', path: '/Services',            permission: 'services.view' },
    { label: 'Recommendations',       icon: '\uD83D\uDCA1', path: '/Recomms',             permission: 'recommendations.view' },
    { label: 'Mappings',              icon: '\uD83D\uDDFA\uFE0F', path: '/Mappings',            permission: 'mappings.view' },
    { label: 'Exercises',             icon: '\uD83E\uDDD8', path: '/Exercises',           permission: 'exercises.view' },
    { label: 'BPConfig',              icon: '\u2699\uFE0F', path: '/BPSelection',         permission: 'bpconfig.view' },
    { label: 'Branches',              icon: '\uD83C\uDFE2', path: '/Branches',            permission: 'branches.view' },
    { label: 'Orgs',                  icon: '\uD83C\uDFE5', path: '/Orgs',                permission: 'orgs.view' },
    { label: 'Attendance Report',     icon: '\uD83D\uDCCA', path: '/AttendanceReport',    permission: 'attendance.view_all', altPermission: 'attendance.review' },
    { label: 'Policies',              icon: '\u2699\uFE0F', path: '/Policies', permission: 'attendance.policy' },
    { label: 'User Management',       icon: '\uD83D\uDC68\u200D\uD83D\uDCBB', path: '/usermgmt',            permission: 'users.view' },
    { label: 'Payroll',               icon: '\uD83D\uDCB0', path: '/Payroll',             permission: 'payroll.view' },
    { label: 'Scan Anomalies',       icon: '\uD83D\uDD0D', path: '/DanglingEntries',     permission: 'admin.scan' },
  ];

  const filterItems = (items) => items.filter(item => {
    if (isSuper(auth)) return true;
    // limitedRemoteAccess users outside geo-fence: show LIMITED_REMOTE_PATHS + explicitly permitted items
    if (isLimitedRemote && !LIMITED_REMOTE_PATHS.includes(item.path)) {
      const hasExplicitPerm = (item.permission && canDo(auth, item.permission)) || (item.altPermission && canDo(auth, item.altPermission));
      if (!hasExplicitPerm) return false;
    }
    if (isSaadhak) return item.saadhak === true;
    // remote access flags grant specific menus regardless of permission
    if (hasRemoteKit && item.path === '/KIT') return true;
    if (hasRemoteExpense && item.path === '/ManageExpenses') return true;
    if (hasRemoteTask && (item.path === '/TaskManagerPage' || item.path === '/ChecklistsDashboard')) return true;
    // Universal items (TherapistDashboard, ListPatients) visible to all staff
    if (item.universal) return true;
    return (auth.allowedScreens || []).includes(item.path) ||
      (item.permission && canDo(auth, item.permission)) ||
      (item.altPermission && canDo(auth, item.altPermission));
  });
  const showAdminSection = isSuper(auth) || adminMenuItems.some(a => (auth.allowedScreens || []).includes(a.path) || (a.permission && canDo(auth, a.permission)) || (a.altPermission && canDo(auth, a.altPermission)));
  const filteredMenu = filterItems(mainMenuItems);
  const filteredAdmin = isSuper(auth) ? filterItems(adminMenuItems) : (showAdminSection ? filterItems(adminMenuItems) : []);
  // Debug: remove after verifying admin menu works
  if (showAdminSection) console.log('[AdminMenu] userRoles:', userRoles, '| adminItems:', adminMenuItems.length, '| filtered:', filteredAdmin.length, filteredAdmin.map(i => i.label));

  // ── Total badge count ────────────────────────────────────────────────────
  const dingCount     = sessionList.filter(s => s.isDinging).length;
  const finishedCount = sessionList.filter(s => s.finishedAt && !s.isDinging).length;
  const idleLiveCount = sessionList.filter(s => !s.isDinging && !s.finishedAt).length;
  const totalBadge    = pendingCount + dingCount + finishedCount + unreadCount + idleLiveCount;

  // ── App Badge API — update mobile home screen badge count ───────────────
  useEffect(() => {
    if (navigator.setAppBadge) {
      if (totalBadge > 0) navigator.setAppBadge(totalBadge).catch(() => {});
      else navigator.clearAppBadge && navigator.clearAppBadge().catch(() => {});
    }
  }, [totalBadge]);

  // ── GDrive disconnected toast (once per screen, resets on navigation) ───
  const [gdriveToast, setGdriveToast] = useState(false);
  const gdriveShownRef = useRef(false);
  const canReconnectDrive = userRoles.some(r => GDRIVE_ROLES.includes(r));

  // Reset the "shown" flag on every route change so toast re-appears on new screen
  useEffect(() => {
    gdriveShownRef.current = false;
    setGdriveToast(false);
  }, [location.pathname]);

  useEffect(() => {
    const handler = () => {
      if (gdriveShownRef.current) return; // only once per screen
      gdriveShownRef.current = true;
      setGdriveToast(true);
    };
    window.addEventListener('gdrive-disconnected', handler);
    return () => window.removeEventListener('gdrive-disconnected', handler);
  }, []); // eslint-disable-line

  const handleGdriveReconnect = () => {
    const baseUrl = (process.env.REACT_APP_URL || '').replace(/\/api\/?$/, '');
    var _ah = (function() { try { return JSON.parse(localStorage.getItem('auth') || '{}'); } catch(e) { return {}; } })();
    var token = _ah.token || sessionStorage.getItem('token') || '';
    const authWin = window.open(baseUrl + '/api/gdrive/auth?token=' + encodeURIComponent(token), 'gdrive_auth', 'width=600,height=700');
    const onMsg = (e) => {
      if (e.data?.type === 'GDRIVE_REAUTH_SUCCESS') {
        setGdriveToast(false);
        window.removeEventListener('message', onMsg);
        if (authWin) authWin.close();
      }
    };
    window.addEventListener('message', onMsg);
    setTimeout(() => window.removeEventListener('message', onMsg), 120000);
  };

  // ── Global ding: play audio when a session dings and SIV is not open ─────
  // When SIV is mounted it plays its own audio via playDing(). When the doctor
  // is on another page the context ticker fires the ding silently — this fills that gap.
  const prevDingCountRef = useRef(0);
  const globalAudioRef   = useRef(null);
  useEffect(() => {
    const isSIVOpen = location.pathname.startsWith('/SessionIpadView');
    if (dingCount > prevDingCountRef.current && !isSIVOpen) {
      try {
        if (!globalAudioRef.current) globalAudioRef.current = new Audio('/session-over-new.mp3');
        const a = globalAudioRef.current;
        a.pause(); a.currentTime = 0; a.volume = 1;
        a.play().catch(() => {});
        setTimeout(() => { try { a.pause(); a.currentTime = 0; } catch (e) {} }, 2000);
      } catch (e) {}
    }
    prevDingCountRef.current = dingCount;
  }, [dingCount, location.pathname]); // eslint-disable-line

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100vh',
    }}>
      <style>{`
        @keyframes pulseDot {
          0%,100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.4; transform: scale(1.5); }
        }
        @keyframes bellShake {
          0%,100% { transform: rotate(0deg); }
          20%      { transform: rotate(-14deg); }
          40%      { transform: rotate(14deg); }
          60%      { transform: rotate(-8deg); }
          80%      { transform: rotate(8deg); }
        }
        .mlHeaderBtn {
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 8px;
          color: #e2e8f0;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 5px 10px;
          font-size: 13px;
          transition: background 0.15s;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .mlHeaderBtn:hover { background: rgba(255,255,255,0.12); }
        .mlPanel {
          position: absolute;
          top: calc(100% + 6px);
          right: 0;
          background: #1e293b;
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 12px;
          box-shadow: 0 12px 40px rgba(0,0,0,0.55);
          min-width: 260px;
          max-width: calc(100vw - 16px);
          z-index: 2000;
          overflow: hidden;
        }
      `}</style>

      {/* ═══════════════════════════════════════════════════════════════════════
          TOP HEADER (flex column — main bar + optional session tabs row)
      ═══════════════════════════════════════════════════════════════════════ */}
      <header style={{
        background:   'linear-gradient(90deg, #0f172a 0%, #1e293b 100%)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        flexShrink:   0,
        zIndex:       1100,
        boxShadow:    '0 2px 12px rgba(0,0,0,0.40)',
        paddingTop:   (isMobile || isTablet) ? 'env(safe-area-inset-top, 0px)' : 0,
      }}>

        {/* ── Main header row ── */}
        <div style={{
          height:     52,
          display:    'flex',
          alignItems: 'center',
          padding:    isMobile ? '0 10px' : '0 16px',
          gap:        isMobile ? 10 : 12,
          overflow:   'visible',
          width:      '100%',
          boxSizing:  'border-box',
        }}>

          {isMobile ? (
            /* ═══════ MOBILE HEADER: Avatar + Namaste + spacer + booking + bell ═══════ */
            <>
              {/* Profile avatar — opens mobile menu */}
              <button
                onClick={() => setShowMobileMenu(true)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0 }}
              >
                <UserAvatar username={auth?.username} size={34} />
              </button>

              {/* Greeting — fills remaining space */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.2 }}>Namaste</div>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#f1f5f9', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {auth?.username || 'User'}
                </div>
              </div>

              {/* Install App (Android) */}
              {installPrompt && (
                <button onClick={handleInstallClick}
                  style={{ background: 'linear-gradient(135deg,#38bdf8,#818cf8)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 11, fontWeight: 700, padding: '5px 10px', cursor: 'pointer', flexShrink: 0 }}>
                  Install
                </button>
              )}

              {/* Attendance Review — for users with review permission */}
              {!isSaadhak && (isSuper(auth) || canDo(auth, 'attendance.view_all') || canDo(auth, 'attendance.review')) && (
                <button onClick={() => navigate('/AttendanceReport')} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', padding: '4px 2px', flexShrink: 0 }} title="Review Attendance">
                  📋
                </button>
              )}

              {/* Booking */}
              <button onClick={() => navigate(isSaadhak ? '/SaadhakDashboard' : '/Cal')} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', padding: '4px 2px', flexShrink: 0 }} title="Appointments">
                📅
              </button>

              {/* Bell with badge */}
              <div style={{ position: 'relative', flexShrink: 0 }} ref={notifPanelRef}>
                <button onClick={handleToggleNotif}
                  style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', padding: '4px 2px', animation: totalBadge > 0 ? 'bellShake 0.6s ease 0s 1' : 'none' }}
                  title="Notifications">
                  🔔
                  {totalBadge > 0 && (
                    <span style={{
                      position: 'absolute', top: 0, right: -4,
                      background: '#ef4444', color: '#fff', borderRadius: '50%',
                      fontSize: 9, fontWeight: 800, width: 16, height: 16,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: '1.5px solid #0f172a', lineHeight: 1,
                    }}>
                      {totalBadge > 9 ? '9+' : totalBadge}
                    </span>
                  )}
                </button>
              </div>
            </>
          ) : (
            /* ═══════ DESKTOP / TABLET HEADER ═══════ */
            <>
              {/* Hamburger */}
              <button
                onClick={() => setMenuOpen(v => !v)}
                style={{
                  background: 'none', border: 'none', color: '#94a3b8',
                  fontSize: 20, cursor: 'pointer', padding: '4px 6px',
                  borderRadius: 6, lineHeight: 1, flexShrink: 0,
                  transition: 'color 0.15s',
                }}
                title={menuOpen ? 'Collapse menu' : 'Expand menu'}
              >
                &#9776;
              </button>

              {/* Brand */}
              <div style={{
                fontWeight: 800, fontSize: 17, letterSpacing: 1,
                background: 'linear-gradient(90deg,#38bdf8,#818cf8)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                userSelect: 'none', flexShrink: 0,
              }}>
                MYC
              </div>

              {/* Separator */}
              <div style={{ width: 1, height: 28, background: 'rgba(255,255,255,0.08)', flexShrink: 0 }} />

              {/* Spacer */}
              <div style={{ flex: 1 }} />

              {/* Install App button (Android) */}
              {installPrompt && (
                <button onClick={handleInstallClick}
                  style={{
                    background: 'linear-gradient(135deg, #38bdf8, #818cf8)',
                    border: 'none', borderRadius: 8, color: '#fff',
                    fontSize: 12, fontWeight: 700, padding: '6px 12px',
                    cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                    boxShadow: '0 2px 8px rgba(56,189,248,0.3)',
                    display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                  <span style={{ fontSize: 14 }}>&#x2B07;</span> Install App
                </button>
              )}
            </>
          )}
        </div>

        {/* ── Notification bell + profile (desktop/tablet): fixed top-right ── */}
        {!isMobile && (
          <>
            <div style={{ position: 'fixed', top: -5, right: 50, zIndex: 1200 }} ref={!isMobile ? notifPanelRef : undefined}>
              <button className="mlHeaderBtn" onClick={handleToggleNotif}
                style={{ fontSize: 17, padding: '5px 8px', animation: totalBadge > 0 ? 'bellShake 0.6s ease 0s 1' : 'none' }}
                title="Notifications">
                🔔
                {totalBadge > 0 && (
                  <span style={{
                    position: 'absolute', top: 3, right: 3,
                    background: '#ef4444', color: '#fff', borderRadius: '50%',
                    fontSize: 9, fontWeight: 800, width: 16, height: 16,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '1.5px solid #0f172a', lineHeight: 1,
                  }}>
                    {totalBadge > 9 ? '9+' : totalBadge}
                  </span>
                )}
              </button>
            </div>

            <div style={{ position: 'fixed', top: -5, right: 130, zIndex: 1200 }} ref={profilePanelRef}>
              <button className="mlHeaderBtn" onClick={handleToggleProfile}
                style={{ padding: '4px 8px 4px 6px', gap: 8 }}>
                <UserAvatar username={auth?.username} size={30} />
                {!isTablet && (
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 700, fontSize: 12.5, color: '#f1f5f9', lineHeight: 1.2 }}>{auth?.username || 'User'}</div>
                    {loginAt && <div style={{ fontSize: 10, color: '#64748b', lineHeight: 1.2 }}>Login: {timeAgo(loginAt)}</div>}
                  </div>
                )}
                <span style={{ color: '#64748b', fontSize: 10 }}>▾</span>
              </button>

              {/* Profile dropdown (desktop/tablet) */}
              {showProfilePanel && (
                <div className="mlPanel" style={{ minWidth: 240, position: 'fixed', top: profilePosRef.current.top, right: 40, left: 'auto' }}>
                  <div style={{ padding: 16, background: 'linear-gradient(135deg,rgba(99,102,241,0.15),rgba(14,165,233,0.10))', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <UserAvatar username={auth?.username} size={42} />
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 14, color: '#f1f5f9' }}>{auth?.username || 'User'}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{displayDesignation}</div>
                    </div>
                  </div>
                  {!isSaadhak && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                      <div style={{ padding: '12px 10px', borderRight: '1px solid rgba(255,255,255,0.06)', cursor: liveCount > 0 ? 'pointer' : 'default' }}
                        onClick={() => { if (liveCount === 0) return; const first = sessionList[0]; if (first) { navigate(`/SessionIpadView/${encodeURIComponent(first.patientCard)}`); setShowProfilePanel(false); } }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: liveCount > 0 ? '#4ade80' : '#475569' }}>{liveCount}</div>
                        <div style={{ fontSize: 10, color: liveCount > 0 ? '#4ade80' : '#64748b', marginTop: 2 }}>Active Sessions</div>
                      </div>
                      <div style={{ padding: '12px 10px', borderRight: '1px solid rgba(255,255,255,0.06)' }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: pendingCount > 0 ? '#f59e0b' : '#475569' }}>{pendingCount}</div>
                        <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>Pending Bookings</div>
                      </div>
                      <div style={{ padding: '12px 10px', cursor: sessionPendingNotifs.length > 0 ? 'pointer' : 'default' }}
                        onClick={() => { if (sessionPendingNotifs.length > 0) { navigate('/Notifications'); setShowProfilePanel(false); } }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: sessionPendingNotifs.length > 0 ? '#f97316' : '#475569' }}>{sessionPendingNotifs.length}</div>
                        <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>Pending Inputs</div>
                      </div>
                    </div>
                  )}
                  {loginAt && (
                    <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 14 }}>🕐</span>
                      <div>
                        <div style={{ fontSize: 10, color: '#64748b' }}>Last logged in</div>
                        <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500, marginTop: 1 }}>
                          {loginAt.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  )}
                  {!isSaadhak && (
                    <div onClick={() => { navigate('/MyAttendance?tab=leaves'); setShowProfilePanel(false); }}
                      style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: '#e2e8f0', fontSize: 13, fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                      <span style={{ fontSize: 15 }}>{'\uD83C\uDFD6\uFE0F'}</span> My Leaves
                    </div>
                  )}
                  {!isSaadhak && (
                    <div onClick={() => { navigate('/MyDocuments'); setShowProfilePanel(false); }}
                      style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: '#e2e8f0', fontSize: 13, fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                      <span style={{ fontSize: 15 }}>{'\uD83D\uDCC4'}</span> My Documents
                    </div>
                  )}
                  <div onClick={() => { navigate('/ChangePassword'); setShowProfilePanel(false); }}
                    style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: '#e2e8f0', fontSize: 13, fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <span style={{ fontSize: 15 }}>🔑</span> Change Password
                  </div>
                  {installPrompt && (
                    <div onClick={() => { handleInstallClick(); setShowProfilePanel(false); }}
                      style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: '#22c55e', fontSize: 13, fontWeight: 700, borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'linear-gradient(135deg,rgba(34,197,94,0.08),rgba(16,185,129,0.04))' }}>
                      <span style={{ fontSize: 15 }}>📲</span> Install App
                    </div>
                  )}
                  <div onClick={() => { navigate('/Logout'); setShowProfilePanel(false); }}
                    style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: '#f87171', fontSize: 13, fontWeight: 600 }}>
                    <span style={{ fontSize: 15 }}>🚪</span> Logout
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Notification dropdown (shared — works for both mobile and desktop) */}
        {showNotifPanel && (
          <div ref={notifDropRef} className="mlPanel" style={{ minWidth: 320, maxWidth: 380, position: 'fixed', top: isMobile ? 56 : notifPosRef.current.top, right: isMobile ? 8 : 20, left: 'auto', zIndex: 2000 }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', fontWeight: 700, fontSize: 13, color: '#94a3b8', letterSpacing: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>NOTIFICATIONS</span>
              {unreadCount > 0 && <span style={{ background: '#ef4444', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 800 }}>{unreadCount}</span>}
            </div>

            {/* Live session alerts (dinging / finished) — keep these at top */}
            {sessionList.filter(s => s.isDinging).map(s => (
              <div key={'ding-' + s.patientCard}
                onClick={() => { sm?.acknowledgeCard(s.patientCard); navigate(`/SessionIpadView/${s.patientCard}`); setShowNotifPanel(false); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(239,68,68,0.06)' }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>⏰</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#fca5a5', fontSize: 12.5, fontWeight: 600 }}>{s.patientName}</div>
                  <div style={{ color: '#94a3b8', fontSize: 10, marginTop: 1 }}>Timer complete — tap to dismiss</div>
                </div>
              </div>
            ))}
            {sessionList.filter(s => s.finishedAt && !s.isDinging).map(s => (
              <div key={'fin-' + s.patientCard}
                onClick={() => { navigate(`/SessionIpadView/${s.patientCard}`); setShowNotifPanel(false); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(250,204,21,0.07)' }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>✅</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#fef08a', fontSize: 12.5, fontWeight: 600 }}>{s.patientName}</div>
                  <div style={{ color: '#94a3b8', fontSize: 10, marginTop: 1 }}>All done — tap to close session</div>
                </div>
              </div>
            ))}

            {/* Recent 5 notifications (all types) */}
            {recentNotifs.map(n => (
              <div key={n._id}
                onClick={() => { navigate('/Notifications', { state: { focusId: n._id } }); setShowNotifPanel(false); }}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.06)', background: n.read ? 'transparent' : 'rgba(249,115,22,0.05)' }}>
                <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>
                  {n.type === 'SESSION_PENDING' ? '⚠️' : n.type === 'ESCALATION' ? '🚨' : n.type === 'GEO_EXIT_ALERT' ? '📍'
                    : n.type === 'ATTENDANCE_ALERT' ? '🕐' : n.type === 'TASK_REMINDER' ? '📋' : n.type === 'TASK_ASSIGNED' ? '📌'
                    : n.type === 'CHECKLIST_MISSED' ? '📝' : n.type === 'BOOKING_ALERT' ? '📩' : n.type === 'MORNING_GREETING' ? '☀️'
                    : n.type === 'PAYMENT_PENDING' ? '💳' : n.type === 'PLAN_APPROVAL' ? '📋' : '🔔'}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#f1f5f9', fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</div>
                  <div style={{ color: '#94a3b8', fontSize: 10.5, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.message}</div>
                  <div style={{ color: '#64748b', fontSize: 10, marginTop: 2 }}>{timeAgo(new Date(n.createdAt))}</div>
                </div>
                {!n.read && <span style={{ marginLeft: 'auto', flexShrink: 0, width: 7, height: 7, borderRadius: '50%', background: '#f97316', marginTop: 6 }} />}
              </div>
            ))}

            {/* Empty state */}
            {recentNotifs.length === 0 && sessionList.filter(s => s.isDinging || s.finishedAt).length === 0 && (
              <div style={{ padding: '24px 16px', textAlign: 'center', color: '#475569', fontSize: 13 }}>No new notifications</div>
            )}

            {/* Show all link */}
            <div onClick={() => { navigate('/Notifications'); setShowNotifPanel(false); }}
              style={{ padding: '10px 16px', textAlign: 'center', color: '#60a5fa', fontSize: 12, cursor: 'pointer', fontWeight: 600, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              Show all notifications →
            </div>
          </div>
        )}

      </header>

      {/* ═══ MOBILE MENU OVERLAY — full screen, triggered by profile avatar ═══ */}
      {isMobile && showMobileMenu && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, background: '#0f172a', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {/* Close button */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <UserAvatar username={auth?.username} size={44} />
              <div>
                <div style={{ fontWeight: 800, fontSize: 16, color: '#f1f5f9' }}>{auth?.username || 'User'}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{displayDesignation}</div>
              </div>
            </div>
            <button onClick={() => setShowMobileMenu(false)}
              style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%', width: 36, height: 36, color: '#94a3b8', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              ✕
            </button>
          </div>

          {/* Stats row */}
          {!isSaadhak && (
            <div style={{ display: 'flex', margin: '8px 16px 0', borderRadius: 12, overflow: 'hidden', background: 'rgba(255,255,255,0.04)' }}>
              <div style={{ flex: 1, padding: '12px 8px', textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.06)' }}
                onClick={() => { if (liveCount > 0 && sessionList[0]) { navigate(`/SessionIpadView/${encodeURIComponent(sessionList[0].patientCard)}`); setShowMobileMenu(false); } }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: liveCount > 0 ? '#4ade80' : '#475569' }}>{liveCount}</div>
                <div style={{ fontSize: 9, color: '#64748b', marginTop: 2 }}>ACTIVE</div>
              </div>
              <div style={{ flex: 1, padding: '12px 8px', textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: pendingCount > 0 ? '#f59e0b' : '#475569' }}>{pendingCount}</div>
                <div style={{ fontSize: 9, color: '#64748b', marginTop: 2 }}>PENDING</div>
              </div>
              <div style={{ flex: 1, padding: '12px 8px', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: totalBadge > 0 ? '#f97316' : '#475569' }}>{totalBadge}</div>
                <div style={{ fontSize: 9, color: '#64748b', marginTop: 2 }}>ALERTS</div>
              </div>
            </div>
          )}

          {/* Menu items */}
          <div style={{ padding: '12px 8px' }}>
            {filteredMenu.map(item => (
              <div key={item.path}
                onClick={() => { navigate(item.path); setShowMobileMenu(false); }}
                style={{
                  padding: '14px 16px', cursor: 'pointer', color: location.pathname === item.path ? '#38bdf8' : '#e2e8f0',
                  fontSize: 12.5, fontWeight: location.pathname === item.path ? 700 : 600,
                  borderRadius: 10, marginBottom: 2,
                  background: location.pathname === item.path ? 'rgba(56,189,248,0.08)' : 'transparent',
                  transition: 'background 0.15s',
                }}>
                <span style={{ marginRight: 10, fontSize: 16 }}>{item.icon}</span>{item.label}
              </div>
            ))}

            {/* Admin submenu (mobile) */}
            {filteredAdmin.length > 0 && (
              <>
                <div
                  onClick={() => setAdminOpen(v => !v)}
                  style={{
                    padding: '14px 16px', cursor: 'pointer',
                    color: adminOpen ? '#818cf8' : '#94a3b8',
                    fontSize: 14, fontWeight: 700, borderRadius: 10, marginBottom: 2,
                    marginTop: 8, borderTop: '1px solid rgba(255,255,255,0.08)',
                    background: adminOpen ? 'rgba(129,140,248,0.06)' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                  <span><span style={{ marginRight: 10, fontSize: 16 }}>{adminOpen ? '\uD83D\uDD13' : '\uD83D\uDD12'}</span>Admin</span>
                  <span style={{ fontSize: 10, transition: 'transform 0.2s', transform: adminOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>{'\u25BC'}</span>
                </div>
                {adminOpen && filteredAdmin.map(item => (
                  <div key={item.path}
                    onClick={() => { navigate(item.path); setShowMobileMenu(false); }}
                    style={{
                      padding: '12px 16px 12px 40px', cursor: 'pointer',
                      color: location.pathname === item.path ? '#38bdf8' : '#94a3b8',
                      fontSize: 11.5, fontWeight: location.pathname === item.path ? 700 : 600,
                      borderRadius: 8, marginBottom: 1,
                      background: location.pathname === item.path ? 'rgba(56,189,248,0.08)' : 'transparent',
                      transition: 'background 0.15s',
                    }}>
                    <span style={{ marginRight: 8, fontSize: 14 }}>{item.icon}</span>{item.label}
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Divider */}
          <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '4px 16px' }} />

          {/* Profile actions */}
          <div style={{ padding: '8px 8px 24px' }}>
            {!isSaadhak && (
              <div onClick={() => { navigate('/MyAttendance?tab=leaves'); setShowMobileMenu(false); }}
                style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: '#e2e8f0', fontSize: 14, fontWeight: 500, borderRadius: 10 }}>
                {'\uD83C\uDFD6\uFE0F'} My Leaves
              </div>
            )}
            {!isSaadhak && (
              <div onClick={() => { navigate('/MyDocuments'); setShowMobileMenu(false); }}
                style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: '#e2e8f0', fontSize: 14, fontWeight: 500, borderRadius: 10 }}>
                {'\uD83D\uDCC4'} My Documents
              </div>
            )}
            <div onClick={() => { navigate('/ChangePassword'); setShowMobileMenu(false); }}
              style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: '#e2e8f0', fontSize: 14, fontWeight: 500, borderRadius: 10 }}>
              🔑 Change Password
            </div>
            {installPrompt && (
              <div onClick={() => { handleInstallClick(); setShowMobileMenu(false); }}
                style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: '#22c55e', fontSize: 14, fontWeight: 700, borderRadius: 10, background: 'linear-gradient(135deg,rgba(34,197,94,0.08),rgba(16,185,129,0.04))' }}>
                📲 Install App
              </div>
            )}
            <div onClick={() => { navigate('/Logout'); setShowMobileMenu(false); }}
              style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: '#f87171', fontSize: 14, fontWeight: 600, borderRadius: 10 }}>
              🚪 Logout
            </div>
          </div>
        </div>
      )}

      <InstallPrompt />

      {/* ── Main body: sidebar + content ─────────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'row', flex: 1, overflow: 'hidden', position: 'relative' }}>

        {/* Mobile/tablet backdrop — closes menu on outside tap */}
        {(isMobile || isTablet) && menuOpen && (
          <div
            onClick={() => setMenuOpen(false)}
            style={{
              position: 'fixed', top: 52, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.45)',
              zIndex: 1040,
            }}
          />
        )}

        <nav
          style={{
            width:           menuOpen ? 225 : ((isMobile || isTablet) ? 0 : 70),
            minWidth:        menuOpen ? 225 : ((isMobile || isTablet) ? 0 : 70),
            backgroundColor: '#1a2332',
            color:           '#ecf0f1',
            height:          '100%',
            boxSizing:       'border-box',
            transition:      'width 0.3s ease, min-width 0.3s ease',
            overflowY:       menuOpen ? 'auto' : 'hidden',
            overflowX:       'hidden',
            display:         (!menuOpen && (isMobile || isTablet)) ? 'none' : 'flex',
            flexDirection:   'column',
            alignItems:      'stretch',
            fontSize:        isTablet ? '1rem' : '0.95rem',
            zIndex:          (isMobile || isTablet) ? 1050 : 1000,
            flexShrink:      0,
            borderRight:     (!menuOpen && (isMobile || isTablet)) ? 'none' : '1px solid rgba(255,255,255,0.06)',
            position:        (isMobile || isTablet) ? 'fixed' : 'relative',
            top:             (isMobile || isTablet) ? 52 : 0,
            left:            0,
            bottom:          (isMobile || isTablet) ? 0 : 'auto',
          }}
          aria-label="Main navigation"
        >
          <ul style={{ paddingLeft: 0, listStyle: 'none', margin: 0, display: 'flex', flexDirection: 'column', paddingTop: 8 }}>
            {filteredMenu.map((item) => (
              <li key={item.path + item.label}>
                <div
                  onClick={() => navigate(item.path)}
                  style={{
                    display:       'flex',
                    alignItems:    'center',
                    padding:       '11px 16px',
                    color:         location.pathname === item.path ? '#38bdf8' : '#cbd5e1',
                    cursor:        'pointer',
                    fontSize:      '0.82rem',
                    borderBottom:  '1px solid rgba(255,255,255,0.04)',
                    whiteSpace:    'nowrap',
                    transition:    'background 0.15s, color 0.15s',
                    background:    location.pathname === item.path ? 'rgba(56,189,248,0.08)' : 'transparent',
                    fontWeight:    location.pathname === item.path ? 700 : 600,
                  }}
                  title={item.label}
                  onMouseEnter={e => { if (location.pathname !== item.path) { e.currentTarget.style.background='rgba(255,255,255,0.06)'; e.currentTarget.style.color='#fff'; } }}
                  onMouseLeave={e => { if (location.pathname !== item.path) { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='#cbd5e1'; } }}
                >
                  <span style={{ width: 28, textAlign: 'center', fontSize: '1.1rem', flexShrink: 0 }}>
                    {item.icon || item.label.charAt(0)}
                  </span>
                  <span style={{ display: menuOpen ? 'inline' : 'none', marginLeft: 10, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.label}
                  </span>
                </div>
              </li>
            ))}

            {/* ── Admin submenu (collapsible) ── */}
            {filteredAdmin.length > 0 && (
              <li>
                <div
                  onClick={() => { if (!menuOpen) setMenuOpen(true); setAdminOpen(v => !v); }}
                  style={{
                    display: 'flex', alignItems: 'center', padding: '11px 16px',
                    color: adminOpen ? '#818cf8' : '#94a3b8', cursor: 'pointer',
                    fontSize: '0.93rem', borderTop: '1px solid rgba(255,255,255,0.08)',
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                    whiteSpace: 'nowrap', transition: 'background 0.15s, color 0.15s',
                    background: adminOpen ? 'rgba(129,140,248,0.06)' : 'transparent',
                    fontWeight: 700,
                  }}
                  title="Admin"
                  onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.06)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = adminOpen ? 'rgba(129,140,248,0.06)' : 'transparent'; }}
                >
                  <span style={{ width: 28, textAlign: 'center', fontSize: '1.1rem', flexShrink: 0 }}>
                    {adminOpen ? '\uD83D\uDD13' : '\uD83D\uDD12'}
                  </span>
                  <span style={{ display: menuOpen ? 'inline' : 'none', marginLeft: 10, flex: 1 }}>
                    Admin
                  </span>
                  <span style={{ display: menuOpen ? 'inline' : 'none', fontSize: 10, marginLeft: 'auto', transition: 'transform 0.2s', transform: adminOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                    {'\u25BC'}
                  </span>
                </div>
                {adminOpen && (
                  <ul style={{ paddingLeft: 0, listStyle: 'none', margin: 0, background: 'rgba(0,0,0,0.15)', maxHeight: '50vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                    {filteredAdmin.map((item) => (
                      <li key={item.path + item.label}>
                        <div
                          onClick={() => navigate(item.path)}
                          style={{
                            display: 'flex', alignItems: 'center',
                            padding: '9px 16px 9px 24px',
                            color: location.pathname === item.path ? '#38bdf8' : '#94a3b8',
                            cursor: 'pointer', fontSize: '0.78rem',
                            borderBottom: '1px solid rgba(255,255,255,0.03)',
                            whiteSpace: 'nowrap', transition: 'background 0.15s, color 0.15s',
                            background: location.pathname === item.path ? 'rgba(56,189,248,0.08)' : 'transparent',
                            fontWeight: location.pathname === item.path ? 700 : 600,
                          }}
                          title={item.label}
                          onMouseEnter={e => { if (location.pathname !== item.path) { e.currentTarget.style.background='rgba(255,255,255,0.06)'; e.currentTarget.style.color='#cbd5e1'; } }}
                          onMouseLeave={e => { if (location.pathname !== item.path) { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='#94a3b8'; } }}
                        >
                          <span style={{ width: 22, textAlign: 'center', fontSize: '0.95rem', flexShrink: 0 }}>
                            {item.icon || item.label.charAt(0)}
                          </span>
                          <span style={{ display: menuOpen ? 'inline' : 'none', marginLeft: 8, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.label}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            )}
          </ul>
        </nav>

        <main style={{
          flexGrow:      1,
          height:        '100%',
          overflowX:     'auto',
          overflowY:     'auto',
          padding:       '0.2rem',
          paddingBottom: liveCount > 0 ? STRIP_HEIGHT + 4 + (isSuper(auth) && liveFooterOpen ? 140 : isSuper(auth) && liveCount > 0 ? 38 : 0) : (isSuper(auth) && liveCount > 0 ? (liveFooterOpen ? 144 : 42) : '0.2rem'),
          boxSizing:     'border-box',
          fontSize:      isTablet ? '1rem' : '0.95rem',
          minWidth:      0,
        }}>
          <Outlet />
        </main>
      </div>
      {/* ── Live Sessions Footer (supervisors only) ───────────────────── */}
      {isSuper(auth) && liveCount > 0 && (
        liveFooterOpen ? (
          /* ── Expanded: full-width status bar ── */
          <div style={{
            position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1050,
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
            borderTop: '2px solid #38bdf8',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.35)',
            overflow: 'hidden',
          }}>
            {/* Header bar */}
            <div
              onClick={() => setLiveFooterOpen(false)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '6px 16px', cursor: 'pointer', userSelect: 'none',
                background: 'rgba(56,189,248,0.08)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14, color: '#38bdf8' }}>▾</span>
                <span style={{ color: '#f1f5f9', fontWeight: 700, fontSize: 13 }}>
                  Live Sessions
                </span>
                <span style={{
                  background: dingCount > 0 ? '#ef4444' : '#22c55e',
                  color: '#fff', borderRadius: 10, padding: '1px 8px',
                  fontSize: 11, fontWeight: 700, minWidth: 18, textAlign: 'center',
                }}>
                  {liveCount}
                </span>
                {dingCount > 0 && (
                  <span style={{ color: '#fbbf24', fontSize: 11, fontWeight: 600 }}>
                    {dingCount} awaiting
                  </span>
                )}
                {finishedCount > 0 && (
                  <span style={{ color: '#a78bfa', fontSize: 11, fontWeight: 600 }}>
                    {finishedCount} finished
                  </span>
                )}
              </div>
              <span
                onClick={(e) => { e.stopPropagation(); navigate('/LiveSessions'); }}
                style={{ color: '#38bdf8', fontSize: 11, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
              >
                Full View →
              </span>
            </div>

            {/* Scrollable session cards */}
            <div style={{
              display: 'flex', gap: 10, padding: '8px 16px',
              overflowX: 'auto', overflowY: 'hidden',
              WebkitOverflowScrolling: 'touch',
            }}>
              {sessionList.map(function(s) {
                var elapsed = s.elapsedSec || 0;
                var mm = Math.floor(elapsed / 60);
                var ss = elapsed % 60;
                var timeStr = mm + ':' + (ss < 10 ? '0' : '') + ss;
                var activeEx = (s.exercises || [])[s.activeIdx];
                var statusColor = s.isDinging ? '#fbbf24' : s.finishedAt ? '#a78bfa' : s.isPaused ? '#fb923c' : '#22c55e';
                var statusLabel = s.isDinging ? 'Awaiting' : s.finishedAt ? 'Done' : s.isPaused ? 'Paused' : 'Running';

                return (
                  <div
                    key={s.patientCard}
                    onClick={function() { navigate('/SessionIpadView/' + encodeURIComponent(s.patientCard)); }}
                    style={{
                      minWidth: 180, maxWidth: 220, flexShrink: 0,
                      background: 'rgba(255,255,255,0.06)', borderRadius: 10,
                      padding: '10px 12px', cursor: 'pointer',
                      border: '1px solid ' + (s.isDinging ? 'rgba(251,191,36,0.4)' : 'rgba(255,255,255,0.08)'),
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
                    onMouseLeave={function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ color: '#f1f5f9', fontWeight: 700, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}>
                        {s.patientName || s.patientCard}
                      </span>
                      <span style={{
                        background: statusColor, color: statusColor === '#fbbf24' ? '#000' : '#fff',
                        borderRadius: 6, padding: '1px 6px', fontSize: 9, fontWeight: 700,
                      }}>
                        {statusLabel}
                      </span>
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: 10, marginBottom: 2 }}>
                      {activeEx ? activeEx.name : 'Session'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ color: '#e2e8f0', fontSize: 16, fontWeight: 700, fontFamily: 'monospace' }}>
                        {timeStr}
                      </span>
                      <span style={{ color: '#64748b', fontSize: 9 }}>
                        {s.username || ''}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* ── Collapsed: small floating expand arrow on right ── */
          <div
            onClick={() => setLiveFooterOpen(true)}
            style={{
              position: 'fixed', bottom: 16, right: 0, zIndex: 1050,
              width: 28, height: 48, cursor: 'pointer',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              borderTop: '2px solid #38bdf8', borderLeft: '2px solid #38bdf8', borderBottom: '2px solid #38bdf8',
              borderRadius: '8px 0 0 8px',
              boxShadow: '-2px 0 12px rgba(0,0,0,0.35)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              userSelect: 'none',
            }}
            title={'Live Sessions (' + liveCount + ')'}
          >
            <span style={{ color: dingCount > 0 ? '#ef4444' : '#38bdf8', fontSize: 14, fontWeight: 700 }}>◂</span>
            {/* Count badge */}
            <span style={{
              position: 'absolute', top: -6, left: -6,
              background: dingCount > 0 ? '#ef4444' : '#22c55e',
              color: '#fff', borderRadius: 8, padding: '0 5px',
              fontSize: 9, fontWeight: 700, minWidth: 14, textAlign: 'center',
              lineHeight: '16px',
            }}>
              {liveCount}
            </span>
          </div>
        )
      )}

      {gdriveToast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 99999, background: '#dc2626', color: '#fff', borderRadius: 10, padding: '12px 20px', fontSize: 13, fontWeight: 600, boxShadow: '0 4px 16px rgba(0,0,0,0.25)', maxWidth: '92vw', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>Storage disconnected — files & Documents operations unavailable.</span>
          {canReconnectDrive ? (
            <button onClick={handleGdriveReconnect} style={{ background: '#fff', color: '#dc2626', border: 'none', borderRadius: 6, padding: '5px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>Reconnect</button>
          ) : (
            <span style={{ fontSize: 11, opacity: 0.8 }}>Ask admin to reconnect</span>
          )}
          <button onClick={() => setGdriveToast(false)} style={{ background: 'rgba(255,255,255,0.25)', border: 'none', color: '#fff', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 13, lineHeight: 1 }}>✕</button>
        </div>
      )}
    </div>
  );
};

export default MainLayout;
